import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { DrillPanel } from "@/components/dashboard/widgets/shared";
import { useEmployeeBillingRate } from "@/hooks/use-employee-billing-rate";
import { useEmployeeKpis } from "@/hooks/use-employee-kpis";
import type { RoleDeviationSummary } from "@/lib/employee-kpis";
import { formatDate, formatHourlyRate, formatHours, formatNOK, formatPct } from "@/lib/format";
import { billingStatus, deviationStatus, TONE_TEXT } from "@/lib/widget-status";
import { cn } from "@/lib/utils";
import { KpiTile } from "./KpiTile";
import { ProjectCodeLabel } from "./ProjectCodeLabel";
import { TabLoading } from "./TabLoading";
import {
  BillingRateDrill,
  GeekShareDrill,
  HourlyRateDrill,
  InternalTimeDrill,
  RoleDeviationDrill,
  SalesDrill,
  SickLeaveDrill,
  type EmployeeKpiDrill,
} from "./EmployeeKpiDrills";
import type { ProjectFilter } from "./ProjectsTab";

interface Props {
  userId: string;
  targetBillingRate: number | null;
  onShowProjects: (filter: ProjectFilter) => void;
}

const TOP_COMPLETED = 5;

const DRILL_TITLES: Record<EmployeeKpiDrill, string> = {
  geek: "Reell GEEK-andel",
  rate: "Reell timepris",
  pl: "Budsjettavvik som PL",
  seller: "Budsjettavvik som salgseier",
  billing: "Faktureringsgrad hittil i år",
  internal: "Interne timer",
  sales: "GEEK etter eierandel",
  sick: "Sykefravær",
};

function deviationTile(summary: RoleDeviationSummary) {
  if (summary.deviationPct == null) {
    return { value: "—", tone: undefined, sub: "Ingen avsluttede prosjekter i rollen" };
  }
  const sign = summary.deviationPct > 0 ? "+" : "";
  return {
    value: `${sign}${formatPct(summary.deviationPct)}`,
    tone: TONE_TEXT[deviationStatus(summary.deviationPct).tone],
    sub: `${formatHours(summary.loggedHours)} brukt / ${formatHours(summary.soldHours)} solgt · ${summary.projectCount} prosjekter`,
  };
}

/** Fanen Nøkkeltall: admin-only KPI-er for én ansatt. Tilgangen håndheves av kalleren. */
export function EmployeeKpiSection({ userId, targetBillingRate, onShowProjects }: Props) {
  const { data, isLoading } = useEmployeeKpis(userId);
  const { data: billing } = useEmployeeBillingRate(userId, new Date().getFullYear());
  const [drill, setDrill] = useState<EmployeeKpiDrill | null>(null);

  if (isLoading) return <TabLoading />;
  if (!data) return null;

  const { year, completed, internalTime, sales, roleDeviation, sickLeave } = data;
  const currentMonth = new Date().getMonth() + 1;
  const billingTarget = targetBillingRate ?? billing?.targetRate ?? null;
  const billingRate = billing?.ytdRate ?? null;
  const delta = completed.deltaVsOrganizationPct;
  const rateTone = delta == null ? undefined : TONE_TEXT[delta >= 0 ? "reached" : "miss"];
  const asLead = deviationTile(roleDeviation.projectLead);
  const asSeller = deviationTile(roleDeviation.salesOwner);
  const topCompleted = [...completed.projects]
    .sort((a, b) => b.geekShare - a.geekShare)
    .slice(0, TOP_COMPLETED);

  return (
    <div className="space-y-6" data-testid="employee-kpis">
      <section className="space-y-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2.5">
          <h2 className="font-display text-[15px] font-semibold">Lønnsomhet</h2>
          <span className="text-sm text-muted-foreground">
            {completed.projects.length} prosjekter avsluttet i {year}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiTile
            label="Reell GEEK-andel"
            value={formatNOK(completed.actualGeekShare)}
            sub={`${formatHours(completed.employeeHours)} egne timer`}
            onClick={() => setDrill("geek")}
          />
          <KpiTile
            label="Reell timepris"
            value={formatHourlyRate(completed.actualHourlyRate)}
            delta={
              delta != null && (
                <span className={cn("flex items-center gap-1", rateTone)}>
                  <SrIcon name={delta >= 0 ? "trending-up" : "trending-down"} className="h-3 w-3" />
                  {formatPct(Math.abs(delta))} {delta >= 0 ? "over" : "under"} teamsnitt
                </span>
              )
            }
            sub={
              completed.organizationHourlyRate != null
                ? `Teamsnitt ${formatHourlyRate(completed.organizationHourlyRate)}`
                : undefined
            }
            onClick={() => setDrill("rate")}
          />
          <KpiTile
            label="Budsjettavvik som PL"
            value={asLead.value}
            valueClassName={asLead.tone}
            sub={asLead.sub}
            onClick={() => setDrill("pl")}
          />
          <KpiTile
            label="Budsjettavvik som salgseier"
            value={asSeller.value}
            valueClassName={asSeller.tone}
            sub={asSeller.sub}
            onClick={() => setDrill("seller")}
          />
        </div>
      </section>

      <section className="space-y-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2.5">
          <h2 className="font-display text-[15px] font-semibold">Timer og salg</h2>
          <span className="text-sm text-muted-foreground">Hittil i {year}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiTile
            label="Faktureringsgrad"
            value={billingRate == null ? "—" : formatPct(billingRate)}
            valueClassName={
              billingRate != null && billingTarget != null
                ? TONE_TEXT[billingStatus(billingRate, billingTarget).tone]
                : undefined
            }
            sub={`Hittil i ${year} · ${billingTarget == null ? "mål ikke satt" : `mål ${formatPct(billingTarget, 0)}`}`}
            onClick={billing ? () => setDrill("billing") : undefined}
          />
          <KpiTile
            label="Interne timer"
            value={formatHours(internalTime.hours)}
            sub={`${internalTime.sharePct == null ? "—" : formatPct(internalTime.sharePct)} av ${formatHours(internalTime.totalLoggedHours)} loggført`}
            onClick={() => setDrill("internal")}
          />
          <KpiTile
            label="GEEK etter eierandel"
            value={formatNOK(sales.attributedGeek)}
            sub={`${sales.projectCount} signerte salg`}
            onClick={() => setDrill("sales")}
          />
          <KpiTile
            label="Sykefravær"
            value={formatHours(sickLeave.hours)}
            sub={`${sickLeave.days.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} arbeidsdager · ${sickLeave.entryCount} registreringer`}
            onClick={() => setDrill("sick")}
          />
        </div>
      </section>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
            <div className="flex flex-wrap items-baseline gap-x-2.5">
              <h2 className="font-display text-base font-semibold">Avsluttede prosjekter {year}</h2>
              <span className="text-sm text-muted-foreground">sortert på GEEK-andel</span>
            </div>
            {completed.projects.length > 0 && (
              <Button variant="link" size="sm" className="h-auto gap-1 p-0" onClick={() => onShowProjects("completed")}>
                Alle avsluttede i Prosjekter
                <SrIcon name="arrow-right" className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {topCompleted.length > 0 ? (
            <div className="overflow-x-auto border-t border-border">
              <table aria-label="Avsluttede prosjekter" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-2 text-left font-medium">Prosjekt</th>
                    <th className="px-3 py-2 text-right font-medium">Ferdigstilt</th>
                    <th className="px-3 py-2 text-right font-medium">Egne timer</th>
                    <th className="px-3 py-2 text-right font-medium">Alle timer</th>
                    <th className="px-3 py-2 text-right font-medium">GEEK-andel</th>
                    <th className="px-5 py-2 text-right font-medium">Reell timepris</th>
                  </tr>
                </thead>
                <tbody>
                  {topCompleted.map((row) => (
                    <tr key={row.projectId} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                      <td className="px-5 py-2.5">
                        <Link to={`/prosjekter/${row.projectId}`} className="font-medium text-primary hover:underline">
                          <ProjectCodeLabel code={row.projectCode} name={row.projectName} />
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 text-right text-muted-foreground">{formatDate(row.completedAt)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatHours(row.employeeHours)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{formatHours(row.totalProjectHours)}</td>
                      <td className={cn("px-3 py-2.5 text-right font-medium tabular-nums", row.geekShare < 0 && "text-destructive")}>
                        {formatNOK(row.geekShare)}
                      </td>
                      <td className={cn("px-5 py-2.5 text-right font-medium tabular-nums", row.actualHourlyRate < 0 && "text-destructive")}>
                        {formatHourlyRate(row.actualHourlyRate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="border-t border-border px-5 py-4 text-sm text-muted-foreground">
              Ingen avsluttede prosjekter med loggførte timer i {year}.
            </p>
          )}
        </CardContent>
      </Card>

      <DrillPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        kicker="Nøkkeltall"
        title={drill ? DRILL_TITLES[drill] : ""}
      >
        <div className="px-4 py-5 sm:px-6">
          {drill === "geek" && <GeekShareDrill completed={completed} year={year} />}
          {drill === "rate" && <HourlyRateDrill completed={completed} />}
          {drill === "pl" && <RoleDeviationDrill summary={roleDeviation.projectLead} role="pl" />}
          {drill === "seller" && <RoleDeviationDrill summary={roleDeviation.salesOwner} role="seller" />}
          {drill === "billing" && billing && (
            <BillingRateDrill
              ytdRate={billingRate}
              target={billingTarget}
              months={billing.months}
              currentMonth={currentMonth}
              year={year}
            />
          )}
          {drill === "internal" && <InternalTimeDrill internalTime={internalTime} year={year} />}
          {drill === "sales" && <SalesDrill sales={sales} year={year} />}
          {drill === "sick" && <SickLeaveDrill sickLeave={sickLeave} year={year} />}
        </div>
      </DrillPanel>
    </div>
  );
}
